/**
 * TITULARIDAD (B1) — pantalla de administración: «¿qué pasa si un alumno no ha
 * pagado?».
 *
 * Tres bloques, en el orden en que se toman las decisiones:
 *  1. **La política de la academia**: se elige un PRESET por nombre (no ocho
 *     casillas) y se ajustan los tres plazos que importan.
 *  2. **La cascada**: qué niveles tienen política propia y cuál manda. Una
 *     política «cerrada» impide que los niveles de abajo la aflojen.
 *  3. **A quién afectaría**: simulación SIN escribir nada. Es lo que evita
 *     encender un feature de dinero a ciegas.
 *
 * Desde la simulación se concede la excepción a un alumno concreto, con motivo y
 * caducidad obligatorios: perdonar dinero deja rastro y no dura para siempre.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { msg } from "@lingui/core/macro";
import { ShieldCheck } from "lucide-solid";
import { entitlementQueryKeys, RECOMMENDED_PRESET_KEY } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label, TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import { apiAuthHeaders, platformApiFetch, SERVER_BASE_URL } from "@/lib/api";

// ── Transporte crudo (estos endpoints no viajan por el cliente tipado) ───────

interface PresetDTO {
  key: string;
  graceDays: number;
  minDebtUsdMinor: number;
  pendingPaymentHoldsDays: number;
  restrictions: string[];
  recommended: boolean;
}

interface PolicyDTO {
  id: string;
  level: string;
  scopeId?: string | null;
  preset: string;
  graceDays: number;
  minDebtUsdMinor: number;
  pendingPaymentHoldsDays: number;
  restrictions: string[];
  locked: boolean;
}

interface PoliciesResponse {
  policies: PolicyDTO[];
  presets: PresetDTO[];
  restrictions: string[];
  enabled: boolean;
}

interface PreviewRow {
  userId: string;
  cohortId: string;
  cohortLabel: string;
  state: string;
  restrictions: string[];
  reasonCode: string;
  sourceLevel: string;
  sourceLabel?: string | null;
  debtUsdMinor: number;
  unblocksAt?: string | null;
}

/** Mensaje humano de un fallo, sin afirmar que sea un `Error`. */
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Transporte crudo con la auth del resto de la app (cookie HttpOnly + selector
 * de cuenta en web; Bearer inyectado por el proxy Rust en nativo).
 *
 * El error del servidor se propaga TAL CUAL: un 403 de «solo el dueño decide la
 * política» o un 400 de «una excepción tiene que caducar» son mensajes que el
 * administrador necesita leer, no un «algo salió mal».
 */
async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await platformApiFetch(`${SERVER_BASE_URL.replace(/\/$/, "")}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...apiAuthHeaders() },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "message" in body && typeof body.message === "string"
        ? body.message
        : `HTTP ${response.status}`;
    throw new Error(message);
  }
  return body;
}

// ── Lectura DEFENSIVA del payload ───────────────────────────────────────────
// El transporte crudo no está tipado, así que el payload se reconstruye campo a
// campo. No es ceremonia: esta pantalla decide sobre dinero y un campo ausente
// no puede convertirse en `undefined` paseándose por la UI.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function strList(value: unknown): string[] {
  const items: unknown[] = Array.isArray(value) ? value : [];
  return items.filter((x): x is string => typeof x === "string");
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toPolicy(raw: unknown): PolicyDTO | null {
  if (!isRecord(raw)) {
    return null;
  }
  return {
    id: str(raw.id),
    level: str(raw.level),
    scopeId: typeof raw.scopeId === "string" ? raw.scopeId : null,
    preset: str(raw.preset),
    graceDays: num(raw.graceDays),
    minDebtUsdMinor: num(raw.minDebtUsdMinor),
    pendingPaymentHoldsDays: num(raw.pendingPaymentHoldsDays),
    restrictions: strList(raw.restrictions),
    locked: raw.locked === true,
  };
}

function toPreset(raw: unknown): PresetDTO | null {
  if (!isRecord(raw)) {
    return null;
  }
  return {
    key: str(raw.key),
    graceDays: num(raw.graceDays),
    minDebtUsdMinor: num(raw.minDebtUsdMinor),
    pendingPaymentHoldsDays: num(raw.pendingPaymentHoldsDays),
    restrictions: strList(raw.restrictions),
    recommended: raw.recommended === true,
  };
}

function toPreviewRow(raw: unknown): PreviewRow | null {
  if (!isRecord(raw)) {
    return null;
  }
  return {
    userId: str(raw.userId),
    cohortId: str(raw.cohortId),
    cohortLabel: str(raw.cohortLabel),
    state: str(raw.state, "entitled"),
    restrictions: strList(raw.restrictions),
    reasonCode: str(raw.reasonCode),
    sourceLevel: str(raw.sourceLevel, "platform"),
    sourceLabel: typeof raw.sourceLabel === "string" ? raw.sourceLabel : null,
    debtUsdMinor: num(raw.debtUsdMinor),
    unblocksAt: typeof raw.unblocksAt === "string" ? raw.unblocksAt : null,
  };
}

function notNull<T>(value: T | null): value is T {
  return value !== null;
}

function asPolicies(raw: unknown): PoliciesResponse {
  if (!isRecord(raw)) {
    return { policies: [], presets: [], restrictions: [], enabled: false };
  }
  return {
    policies: list(raw.policies).map(toPolicy).filter(notNull),
    presets: list(raw.presets).map(toPreset).filter(notNull),
    restrictions: strList(raw.restrictions),
    enabled: raw.enabled === true,
  };
}

function asPreviewRows(raw: unknown): PreviewRow[] {
  if (!isRecord(raw)) {
    return [];
  }
  return list(raw.rows).map(toPreviewRow).filter(notNull);
}

function usdMinor(value: number): string {
  return `$${(value / 100).toFixed(2)}`;
}

// ── Pantalla ─────────────────────────────────────────────────────────────────

export const EntitlementAdmin: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const queryClient = useQueryClient();

  const [preset, setPreset] = createSignal<string>(RECOMMENDED_PRESET_KEY);
  const [graceDays, setGraceDays] = createSignal("7");
  const [minDebt, setMinDebt] = createSignal("100");
  const [holds, setHolds] = createSignal("5");
  const [locked, setLocked] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const [grantFor, setGrantFor] = createSignal<PreviewRow | null>(null);
  const [reason, setReason] = createSignal("");
  const [expiresOn, setExpiresOn] = createSignal("");

  const policies = createQuery(() => ({
    queryKey: entitlementQueryKeys.policies(activeOrgId()),
    enabled: Boolean(activeOrgId()),
    queryFn: async () =>
      asPolicies(await request(`/api/academy/${activeOrgId() ?? ""}/entitlement/policies`)),
  }));

  const preview = createQuery(() => ({
    queryKey: entitlementQueryKeys.preview(activeOrgId()),
    enabled: Boolean(activeOrgId()),
    queryFn: async () =>
      asPreviewRows(await request(`/api/academy/${activeOrgId() ?? ""}/entitlement/preview`)),
  }));

  const presetOptions = createMemo(() =>
    (policies.data?.presets ?? []).map((p) => ({
      value: p.key,
      label: p.recommended ? `${p.key} · ${t(msg`recommended`)}` : p.key,
    })),
  );

  /** Al elegir un preset, sus plazos pre-rellenan los campos (se pueden ajustar). */
  const applyPreset = (key: string) => {
    setPreset(key);
    const p = (policies.data?.presets ?? []).find((x) => x.key === key);
    if (p) {
      setGraceDays(String(p.graceDays));
      setMinDebt(String(p.minDebtUsdMinor));
      setHolds(String(p.pendingPaymentHoldsDays));
    }
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: entitlementQueryKeys.all });
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      await request(`/api/academy/${activeOrgId() ?? ""}/entitlement/policies`, {
        method: "PUT",
        body: JSON.stringify({
          level: "org",
          preset: preset(),
          graceDays: Number(graceDays()) || 0,
          minDebtUsdMinor: Number(minDebt()) || 0,
          pendingPaymentHoldsDays: Number(holds()) || 0,
          locked: locked(),
        }),
      });
      showToast(t(msg`Policy saved`), "success");
      await refresh();
    } catch (error) {
      showToast(errorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const deletePolicy = async (id: string) => {
    try {
      await request(`/api/academy/${activeOrgId() ?? ""}/entitlement/policies/${id}`, {
        method: "DELETE",
      });
      showToast(t(msg`Policy removed; this level inherits again`), "success");
      await refresh();
    } catch (error) {
      showToast(errorMessage(error), "error");
    }
  };

  const grantException = async () => {
    const row = grantFor();
    if (!row) {
      return;
    }
    try {
      await request(`/api/academy/${activeOrgId() ?? ""}/entitlement/grants`, {
        method: "POST",
        body: JSON.stringify({
          userId: row.userId,
          kind: "exception",
          scopeLevel: "org",
          reason: reason(),
          expiresOn: expiresOn(),
        }),
      });
      showToast(t(msg`Exception granted`), "success");
      setGrantFor(null);
      setReason("");
      setExpiresOn("");
      await refresh();
    } catch (error) {
      showToast(errorMessage(error), "error");
    }
  };

  return (
    <div class="space-y-6" data-testid="entitlement-admin">
      {/* 1 · La política de la academia */}
      <Card>
        <CardHeader>
          <CardTitle>{t(msg`Your academy's policy`)}</CardTitle>
          <CardDescription>
            {t(
              msg`What happens when a student hasn't paid. Pick a preset and adjust the three timings that matter. Nothing is applied until the entitlement feature is on.`,
            )}
          </CardDescription>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="space-y-1">
              <Label>{t(msg`Preset`)}</Label>
              <Select
                options={presetOptions()}
                value={preset()}
                onChange={applyPreset}
                aria-label={t(msg`Preset`)}
              />
            </div>
            <div class="space-y-1">
              <Label for="ent-grace">{t(msg`Grace days after the due date`)}</Label>
              <TextField
                id="ent-grace"
                type="number"
                min="0"
                value={graceDays()}
                onInput={(e) => {
                  setGraceDays(e.currentTarget.value);
                }}
              />
            </div>
            <div class="space-y-1">
              <Label for="ent-min-debt">{t(msg`Minimum debt to block (US cents)`)}</Label>
              <TextField
                id="ent-min-debt"
                type="number"
                min="0"
                value={minDebt()}
                onInput={(e) => {
                  setMinDebt(e.currentTarget.value);
                }}
              />
            </div>
            <div class="space-y-1">
              <Label for="ent-holds">{t(msg`Days a payment awaiting verification holds`)}</Label>
              <TextField
                id="ent-holds"
                type="number"
                min="0"
                value={holds()}
                onInput={(e) => {
                  setHolds(e.currentTarget.value);
                }}
              />
            </div>
          </div>
          <div class="flex items-center gap-3">
            <Switch checked={locked()} onCheckedChange={setLocked} />
            <span class="text-sm text-muted-foreground">
              {t(msg`Locked: courses and cohorts cannot loosen this policy`)}
            </span>
          </div>
          <div class="flex justify-end">
            <Button
              data-testid="entitlement-save-policy"
              disabled={saving()}
              onClick={() => {
                void savePolicy();
              }}
            >
              {t(msg`Save policy`)}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2 · La cascada */}
      <Card>
        <CardHeader>
          <CardTitle>{t(msg`Inheritance`)}</CardTitle>
          <CardDescription>
            {t(
              msg`Platform → academy → program → course → cohort → student. The most specific level wins, unless a level above is locked.`,
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Show
            when={(policies.data?.policies.length ?? 0) > 0}
            fallback={
              <p class="text-sm text-muted-foreground">
                {t(msg`No level has its own policy yet: nobody is blocked.`)}
              </p>
            }
          >
            <ul class="divide-y">
              <For each={policies.data?.policies ?? []}>
                {(p) => (
                  <li class="flex items-center justify-between gap-3 py-2">
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <Badge>{p.level}</Badge>
                        <span class="text-sm font-medium">{p.preset}</span>
                        <Show when={p.locked}>
                          <Badge variant="outline">{t(msg`locked`)}</Badge>
                        </Show>
                      </div>
                      <p class="truncate text-xs text-muted-foreground">
                        {p.restrictions.join(", ") || t(msg`nothing is withheld`)}
                      </p>
                    </div>
                    <Show when={p.level !== "platform"}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          void deletePolicy(p.id);
                        }}
                      >
                        {t(msg`Inherit`)}
                      </Button>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </CardContent>
      </Card>

      {/* 3 · A quién afectaría */}
      <Card>
        <CardHeader>
          <CardTitle>{t(msg`Who this would affect`)}</CardTitle>
          <CardDescription>
            {t(
              msg`Simulation against real invoices. Nothing is written and nobody is blocked by opening this screen.`,
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Show when={!preview.isPending} fallback={<Skeleton class="h-24 w-full" />}>
            <Show
              when={(preview.data?.length ?? 0) > 0}
              fallback={
                <EmptyState
                  icon={ShieldCheck}
                  title={t(msg`Nobody would be blocked`)}
                  description={t(msg`No enrolled student matches this policy right now.`)}
                />
              }
            >
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-left text-muted-foreground">
                      <th class="py-2 pr-3">{t(msg`Cohort`)}</th>
                      <th class="py-2 pr-3">{t(msg`Status`)}</th>
                      <th class="py-2 pr-3">{t(msg`Withheld`)}</th>
                      <th class="py-2 pr-3">{t(msg`Debt`)}</th>
                      <th class="py-2 pr-3">{t(msg`Comes from`)}</th>
                      <th class="py-2" />
                    </tr>
                  </thead>
                  <tbody class="divide-y">
                    <For each={preview.data ?? []}>
                      {(row) => (
                        <tr>
                          <td class="py-2 pr-3">{row.cohortLabel}</td>
                          <td class="py-2 pr-3">
                            <Badge variant={row.state === "restricted" ? "destructive" : "outline"}>
                              {row.state}
                            </Badge>
                          </td>
                          <td class="py-2 pr-3 text-muted-foreground">
                            {row.restrictions.join(", ") || "—"}
                          </td>
                          <td class="py-2 pr-3">{usdMinor(row.debtUsdMinor)}</td>
                          <td class="py-2 pr-3 text-muted-foreground">
                            {row.sourceLabel ?? row.sourceLevel}
                          </td>
                          <td class="py-2">
                            <Show when={row.state !== "entitled"}>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid="entitlement-grant-open"
                                onClick={() => {
                                  setGrantFor(row);
                                }}
                              >
                                {t(msg`Grant exception`)}
                              </Button>
                            </Show>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </Show>
        </CardContent>
      </Card>

      {/* Excepción: motivo y caducidad OBLIGATORIOS (perdonar dinero se audita). */}
      <Dialog
        open={grantFor() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setGrantFor(null);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t(msg`Grant an exception`)}</DialogTitle>
          <DialogDescription>
            {t(
              msg`The student gets access back until the date you set. Both the reason and the expiry are recorded: a permanent exception is revenue nobody looks at again.`,
            )}
          </DialogDescription>
        </DialogHeader>
        <div class="space-y-3">
          <div class="space-y-1">
            <Label for="ent-reason">{t(msg`Reason`)}</Label>
            <TextField
              id="ent-reason"
              value={reason()}
              onInput={(e) => {
                setReason(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-1">
            <Label for="ent-expires">{t(msg`Expires on`)}</Label>
            <TextField
              id="ent-expires"
              type="date"
              value={expiresOn()}
              onInput={(e) => {
                setExpiresOn(e.currentTarget.value);
              }}
            />
          </div>
          <div class="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setGrantFor(null);
              }}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              data-testid="entitlement-grant-confirm"
              disabled={reason().trim().length === 0 || expiresOn().length === 0}
              onClick={() => {
                void grantException();
              }}
            >
              {t(msg`Grant`)}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
